import { isBooleanFalse, isBooleanTrue, isListNotEmpty, isNotNullOrUndefined, isNumberGreaterThanZero, isObjectNotEmpty, isStringEmpty, isStringNotEmpty, isStringNotEmptyOrZeroLength } from "../utils/objectUtils";
import { convertToSlug } from "../utils/stringFormatter";
import { create, getAll, getByDoubleQuery, getById, getByIdQuery, getBySingleQuery, remove, update, updateField } from '../utils/dbUtils';
import { collection, endBefore, getCountFromServer, getDocs, limit, limitToLast, orderBy, query, startAfter, where } from 'firebase/firestore';
import { db, loadStorage } from '../firebase';
import { asTimeZone } from '../utils/dateFormatter';
import moment from "moment-timezone";
import { v4 as uuid } from 'uuid';
import { toLower } from "lodash";
import StatusHistory from "./StatusHistory";

class Article {
    static collectionName = 'articles';

    title = null;
    slug = convertToSlug(this.title);
    categoryId = null;
    content = null;
    excerpt = null;
    coverImageRefId = null; // cover image
    #coverImage = null;

    status = "DRAFT"; // Enum ArticleStatuses DRAFT, SCHEDULED, PUBLISHED
    statusHistory = []; // list of StatusHistory, historical changes to status

    tags = [];

    authorName = null;
    authorAbout = null;
    authorImageRefId = null;
    #authorImage = null;

    displayDate = null;

    isPublished = false;
    publishedAt = null;

    isScheduled = false;
    scheduledAt = null;

    readingLength = 0; // in minutes

    constructor(campaign) {
        if (isObjectNotEmpty(campaign)) {
            if (isStringNotEmpty(campaign.title)) { this.title = campaign.title; }
            if (isStringNotEmpty(campaign.slug)) { this.slug = campaign.slug; }
            else if (isStringNotEmpty(campaign.name)) { this.slug = convertToSlug(campaign.name); }
            if (isStringNotEmpty(campaign.categoryId)) { this.categoryId = campaign.categoryId; }
            if (isStringNotEmpty(campaign.content)) { this.content = campaign.content; }
            if (isStringNotEmpty(campaign.excerpt)) { this.excerpt = campaign.excerpt; }
            if (isNotNullOrUndefined(campaign.coverImage)) { this.#coverImage = campaign.coverImage; }
            if (isStringNotEmpty(campaign.coverImageRefId)) { this.coverImageRefId = campaign.coverImageRefId; }

            if (isStringNotEmpty(campaign.status)) { this.status = campaign.status; }
            if (isListNotEmpty(campaign.statusHistory)) { this.statusHistory = campaign.statusHistory; }
            else if (isStringNotEmpty(this.status)) { this.statusHistory = StatusHistory.addNewStatus(this.status); }

            if (isListNotEmpty(campaign.tags)) { this.tags = campaign.tags; }

            if (isStringNotEmpty(campaign.authorName)) { this.authorName = campaign.authorName; }
            if (isStringNotEmpty(campaign.authorAbout)) { this.authorAbout = campaign.authorAbout; }
            if (isNotNullOrUndefined(campaign.authorImage)) { this.#authorImage = campaign.authorImage; }
            if (isStringNotEmpty(campaign.authorImageRefId)) { this.authorImageRefId = campaign.authorImageRefId; }

            if (isNotNullOrUndefined(campaign.displayDate)) { this.displayDate = campaign.displayDate; }

            if (isBooleanTrue(campaign.isPublished)) { this.isPublished = true; }
            if (isNotNullOrUndefined(campaign.publishedAt)) { this.publishedAt = campaign.publishedAt; }

            if (isBooleanTrue(campaign.isScheduled)) { this.isScheduled = true; }
            if (isNotNullOrUndefined(campaign.scheduledAt)) { this.scheduledAt = campaign.scheduledAt; }

            if (isNumberGreaterThanZero(campaign.readingLength)) { this.readingLength = campaign.readingLength; }
        }
    }

    toDto = () => {
        return {
            title: this.title,
            slug: this.slug,
            categoryId: this.categoryId,
            content: this.content,
            excerpt: this.excerpt,
            coverImageRefId: this.coverImageRefId,

            status: this.status,
            statusHistory: this.statusHistory,

            tags: this.tags,
            authorName: this.authorName,
            authorAbout: this.authorAbout,
            authorImageRefId: this.authorImageRefId,

            displayDate: this.displayDate,

            isPublished: this.isPublished,
            publishedAt: this.publishedAt,

            isScheduled: this.isScheduled,
            scheduledAt: this.scheduledAt,

            readingLength: this.readingLength
        };
    }

    isValid = () => {
        if (isStringEmpty(this.title)) {
            throw new Error('Article title is required.');
        }

        return true;
    }

    static addFile = async (file) => {
        if (isNotNullOrUndefined(file)) {
            try {
                const fileName = uuid() + '.' + this.getFileExt(file);
                const { storage, ref, uploadBytes } = await loadStorage();
                const storageRef = ref(storage, 'articles/' + fileName);

                return await uploadBytes(storageRef, file);
            } catch (err) {
                console.log(err);
            }
        }

        return null;
    }

    static getFileExt = (file) => {
        const lastDot = file.name.lastIndexOf('.');
        //const fileName = fullFileName.substring(0, lastDot);
        const ext = file.name.substring(lastDot + 1);


        return toLower(ext);
    }

    static uploadImage = async (file) => {
        if (isNotNullOrUndefined(file)) {
            const uploadRes = await Article.addFile(file);
            // console.log(uploadRes);
            if (isObjectNotEmpty(uploadRes)) {
                return uploadRes.ref.fullPath;
            }
        }

        return null;
    }

    create = async () => {
        if (this.isValid()) {
            this.coverImageRefId = await Article.uploadImage(this.#coverImage);
            this.authorImageRefId = await Article.uploadImage(this.#authorImage);

            return await create(Article.collectionName, this.toDto());
        }
    }

    static updateField = async (id, fieldName, newValue) => {
        if (isStringNotEmpty(id)) {
            return await updateField(Article.collectionName, id, fieldName, newValue);
        } else {
            throw new Error("id is required.");
        }
    }

    static update = async (id, updatedArticle, oldArticle) => {
        const updatedObj = new Article(updatedArticle);
        if (updatedObj.isValid() && id === updatedArticle.id) {
            // update the image if a new image is provided
            if (isNotNullOrUndefined(updatedObj.#coverImage)) {
                updatedObj.coverImageRefId = await Article.uploadImage(updatedObj.#coverImage);
            }
            if (isNotNullOrUndefined(updatedObj.#authorImage)) {
                updatedObj.authorImageRefId = await Article.uploadImage(updatedObj.#authorImage);
            }

            const success = await update(Article.collectionName, id, updatedObj.toDto());
            if (isBooleanTrue(success)) {
                return { ...oldArticle, ...updatedObj };
            }
        } else {
            throw new Error('id and id field of the updated article must match.')
        }

        return null;
    }

    static updateCoverImage = async (id, file) => {
        if (isNotNullOrUndefined(file)) {
            const uploadRes = await Article.addFile(file);
            // console.log(uploadRes);
            if (isObjectNotEmpty(uploadRes)) {
                let imageRefId = uploadRes.ref.fullPath;
                let success = await Article.updateField(id, 'coverImageRefId', imageRefId);
                if (isBooleanTrue(success)) {
                    return imageRefId;
                }
            }
        }

        return null;
    }

    static updateAuthorImage = async (id, file) => {
        if (isNotNullOrUndefined(file)) {
            const uploadRes = await Article.addFile(file);
            // console.log(uploadRes);
            if (isObjectNotEmpty(uploadRes)) {
                let imageRefId = uploadRes.ref.fullPath;
                let success = await Article.updateField(id, 'authorImageRefId', imageRefId);
                if (isBooleanTrue(success)) {
                    return imageRefId;
                }
            }
        }

        return null;
    }

    static delete = async (id) => {
        if (isStringNotEmpty(id)) {
            return await remove(Article.collectionName, id);
        } else {
            throw new Error("id is required.");
        }
    }

    static getById = async (id) => {
        if (isStringNotEmpty(id)) {
            return await getById(Article.collectionName, id);
        } else {
            throw new Error("id is required.");
        }
    }

    static getAll = async () => {
        return await getAll(Article.collectionName);
    }

    static getByField = async (fieldName, fieldValue, comparer = '==') => {
        return await getBySingleQuery(Article.collectionName, fieldName, fieldValue, comparer);
    }

    static getByCategoryId = async (categoryId) => {
        return await getBySingleQuery(Article.collectionName, 'categoryId', categoryId, '==');
    }

    static getByCategoryIdAndStatus = async (categoryId, status) => {
        if (isListNotEmpty(status)) {
            return await getByDoubleQuery(Article.collectionName, 'categoryId', categoryId, '==', 'status', status, 'in');
        }

        return await getByDoubleQuery(Article.collectionName, 'categoryId', categoryId, '==', 'status', status, '==');
    }

    static getByIds = async (ids = []) => {
        return await getByIdQuery(Article.collectionName, ids, 'in');
    }

    static getCountByField = async (fieldName, fieldValue, comparer = '==') => {
        let queryConstraints = [];

        queryConstraints.push(where(fieldName, comparer, fieldValue));

        let q = query(collection(db, Article.collectionName), ...queryConstraints);

        const snapShot = await getCountFromServer(q);

        return snapShot.data().count;
    }

    static getCount = async (queryParameters) => {
        const { status, categoryId, isPublished, isScheduled, start, end } = queryParameters;

        let queryConstraints = [];

        if (isListNotEmpty(categoryId)) {
            queryConstraints.push(where('categoryId', 'in', categoryId));
        } else if (isStringNotEmptyOrZeroLength(categoryId)) {
            queryConstraints.push(where('categoryId', '==', categoryId));
        }

        if (isListNotEmpty(status)) {
            queryConstraints.push(where('status', 'in', status));
        } else if (isStringNotEmptyOrZeroLength(status)) {
            queryConstraints.push(where('status', '==', status));
        }

        if (isBooleanTrue(isPublished)) {
            queryConstraints.push(where('isPublished', '==', true));
        } else if (isBooleanFalse(isPublished)) {
            queryConstraints.push(where('isPublished', '==', false));
        }

        if (isBooleanTrue(isScheduled)) {
            queryConstraints.push(where('isScheduled', '==', true));
        } else if (isBooleanFalse(isScheduled)) {
            queryConstraints.push(where('isScheduled', '==', false));
        }

        if (isNotNullOrUndefined(start)) {
            let startDate = asTimeZone(moment(new Date(start)), 'US/Pacific').startOf('day').utc().toDate();
            queryConstraints.push(where('displayDate', '>=', startDate));
        }

        if (isNotNullOrUndefined(end)) {
            let endDate = asTimeZone(moment(new Date(end)), 'US/Pacific').endOf('day').utc().toDate();
            queryConstraints.push(where('displayDate', '<=', endDate));
        }

        let q = query(collection(db, Article.collectionName), ...queryConstraints);

        const snapShot = await getCountFromServer(q);

        return snapShot.data().count;
    }

    static getPaginated = async (queryParameters, pageSize = 25, firstVisible, lastVisible, orderByField = 'displayDate') => {
        const { status, categoryId, isPublished, isScheduled, start, end } = queryParameters;

        let queryConstraints = [];

        if (isListNotEmpty(categoryId)) {
            queryConstraints.push(where('categoryId', 'in', categoryId));
        } else if (isStringNotEmptyOrZeroLength(categoryId)) {
            queryConstraints.push(where('categoryId', '==', categoryId));
        }

        if (isListNotEmpty(status)) {
            queryConstraints.push(where('status', 'in', status));
        } else if (isStringNotEmptyOrZeroLength(status)) {
            queryConstraints.push(where('status', '==', status));
        }

        if (isBooleanTrue(isPublished)) {
            queryConstraints.push(where('isPublished', '==', true));
        } else if (isBooleanFalse(isPublished)) {
            queryConstraints.push(where('isPublished', '==', false));
        }

        if (isBooleanTrue(isScheduled)) {
            queryConstraints.push(where('isScheduled', '==', true));
        } else if (isBooleanFalse(isScheduled)) {
            queryConstraints.push(where('isScheduled', '==', false));
        }

        if (isNotNullOrUndefined(start)) {
            let startDate = asTimeZone(moment(new Date(start)), 'US/Pacific').startOf('day').utc().toDate();
            queryConstraints.push(where('displayDate', '>=', startDate));
        }

        if (isNotNullOrUndefined(end)) {
            let endDate = asTimeZone(moment(new Date(end)), 'US/Pacific').endOf('day').utc().toDate();
            queryConstraints.push(where('displayDate', '<=', endDate));
        }

        let q = null;
        if (isObjectNotEmpty(firstVisible)) {
            q = query(collection(db, Article.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limitToLast(pageSize), endBefore(firstVisible[orderByField], firstVisible['id']));
        } else if (isObjectNotEmpty(lastVisible)) {
            q = query(collection(db, Article.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limit(pageSize), startAfter(lastVisible[orderByField], lastVisible['id']));
        } else {
            q = query(collection(db, Article.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limit(pageSize));
        }

        let snapShot = await getDocs(q);
        return snapShot.docs.map((doc) => {
            return {
                id: doc.id,
                ...doc.data()
            };
        });
    }

    static get = async (queryParameters) => {
        let allRecords = [];
        try {
            let next = null;
            let totalPages = 0;

            let totalCount = await this.getCount(queryParameters);
            totalPages = Math.ceil(totalCount / 10000.0);
            // console.log('totalCount: ' + totalCount);
            // console.log('totalPages: ' + totalPages);
            let pages = [];
            for (let i = 0; i < totalPages; i++) {
                pages.push(i + 1);
            }
            for (const page of pages) {
                // console.log('starting page ' + page);

                let list = await this.getPaginated(queryParameters, 10000, null, next, 'displayDate');

                // console.log(list.length);
                allRecords = [...allRecords, ...list];

                // console.log(allRecords.length);

                // console.log('go to next page');
                next = list[list.length - 1];
            }
        } catch (error) {
            console.log(error);
        }

        return allRecords;
    }
};

export default Article;